import { useCallback } from 'react';
import { DEFAULT_IMAGE_SLIDE_DELAY, TRANSITION_DURATION } from './constants';
import { playHandler } from '../videoMedia/helpers/safePlayback';

// Constants
const FOCUSABLE_ELEMENTS_SELECTOR =
  'a, button, input, textarea, select, details, [tabindex]:not(video, .vjs-poster, .video-js)';
const VIDEO_PLAYER_SELECTOR = '[data-vjs-player]';

// Helper to get video player from a slide element
const getVideoPlayer = slide =>
  slide?.querySelector?.(VIDEO_PLAYER_SELECTOR)?.player || null;

// Helper to stop and reset a video player
const stopAndResetVideo = player => {
  if (player) {
    player.pause();
    player.currentTime(0);
    player.setState({ pausedOnLeave: false });
  }
};

// Helper to clear autoplay timers
const clearAutoplayTimers = (timerRef, timeLeftRef) => {
  if (timerRef.current) {
    clearTimeout(timerRef.current);
  }
  if (timeLeftRef.current) {
    cancelAnimationFrame(timeLeftRef.current);
  }
};

/**
 * Carousel utility functions
 */
export const useCarouselUtils = ({
  carouselRef,
  currentTransformIndex,
  slides,
  activeSlideIndex,
  translations,
  isTransitioning,
  setIsTransitioning,
  setActiveSlideIndex,
  setCurrentTransformIndex,
  timerRef,
  timeLeftRef,
  isAutoPlaying,
  getSlideDelay,
  progressCircle,
  prefersReducedMotion,
  transitionTimeoutRef,
  setTransitionDirection,
}) => {
  // Video management functions
  // Note: We intentionally DON'T try to play videos from the carousel.
  // The videoMedia's pause-on-leave plugin handles autoplay via IntersectionObserver.
  // The carousel only stops/resets inactive videos to avoid conflicts.

  const getActiveSlideVideoPlayer = useCallback(() => {
    if (!carouselRef.current) return null;
    const activeSlide = carouselRef.current.children[currentTransformIndex];
    return getVideoPlayer(activeSlide);
  }, [carouselRef, currentTransformIndex]);

  const pauseVideo = useCallback(() => {
    getActiveSlideVideoPlayer()?.pause();
  }, [getActiveSlideVideoPlayer]);

  const resumeVideo = useCallback(() => {
    playHandler(getActiveSlideVideoPlayer());
  }, [getActiveSlideVideoPlayer]);

  // Restart video from beginning (reset to 0 and play)
  const restartVideo = useCallback(() => {
    const player = getActiveSlideVideoPlayer();
    if (player) {
      player.currentTime(0);
      playHandler(player);
    }
  }, [getActiveSlideVideoPlayer]);

  /**
   * Stop and reset videos on INACTIVE slides only.
   * This prevents old videos from continuing to play when they slide out of view.
   * We don't touch the active slide - the pause-on-leave plugin will handle autoplay.
   */
  const stopInactiveVideos = useCallback(() => {
    if (!carouselRef.current) return;

    const realSlideCount = slides?.length || 0;

    // DOM structure: [lastDuplicate (0), ...realSlides (1 to N), firstDuplicate (N+1)]
    Array.from(carouselRef.current.children).forEach((slide, domIndex) => {
      const isDuplicate = domIndex === 0 || domIndex === realSlideCount + 1;
      const isActiveSlide = domIndex === currentTransformIndex;

      // Skip duplicates and active slide
      if (isDuplicate || isActiveSlide) return;

      stopAndResetVideo(getVideoPlayer(slide));
    });
  }, [carouselRef, slides?.length, currentTransformIndex]);

  /**
   * Stop and reset ALL videos including the active slide.
   * Used for component unmount cleanup to ensure proper disposal.
   */
  const stopAllVideos = useCallback(() => {
    if (!carouselRef.current) return;

    Array.from(carouselRef.current.children).forEach(slide => {
      stopAndResetVideo(getVideoPlayer(slide));
    });
  }, [carouselRef]);

  const isActiveSlideVideo = useCallback(() => {
    return slides[activeSlideIndex]?.containerType?.toUpperCase() === 'VIDEO';
  }, [slides, activeSlideIndex]);

  // Helper functions
  const updateFocusableElements = useCallback(
    slideIndex => {
      if (!carouselRef.current) {
        return;
      }

      const slideElements = Array.from(carouselRef.current.children);
      const realSlideCount = slides?.length || 0;

      // DOM structure: [lastDuplicate, ...realSlides, firstDuplicate]
      // So real slides are at DOM indices 1 through realSlideCount
      slideElements.forEach((slide, domIndex) => {
        const focusableElements = slide?.querySelectorAll(
          FOCUSABLE_ELEMENTS_SELECTOR,
        );

        // Check if this is a duplicate slide (first or last in DOM)
        const isDuplicate = domIndex === 0 || domIndex === realSlideCount + 1;

        // Real slides start at DOM index 1, so active slide is at domIndex = slideIndex + 1
        const isActive = !isDuplicate && domIndex === slideIndex + 1;

        focusableElements.forEach(el => {
          el.setAttribute('tabindex', isActive ? '0' : '-1');
        });

        slide?.setAttribute('aria-hidden', !isActive);

        // For duplicates, don't set a meaningful aria-label (they're hidden anyway)
        // For real slides, calculate the actual slide number (1-based)
        if (!isDuplicate) {
          const actualSlideNumber = domIndex; // domIndex 1 = slide 1, domIndex 2 = slide 2, etc.
          const slideData = slides[domIndex - 1]; // slides array is 0-indexed
          const isVideo = slideData?.containerType?.toUpperCase() === 'VIDEO';
          const mediaType = isVideo ? 'video' : 'image';
          const slideTitle = slideData?.titleProps?.text;
          const titleSuffix = slideTitle ? `: ${slideTitle}` : '';

          const baseLabel =
            translations.autoplaySlideNumber
              ?.replace('{slideNum}', actualSlideNumber)
              ?.replace('{slidesTotalNum}', realSlideCount) ||
            `Slide ${actualSlideNumber} of ${realSlideCount}`;
          const label = `${baseLabel}, ${mediaType}${titleSuffix}`;
          slide.setAttribute('aria-label', label);
        }
      });
    },
    [carouselRef, translations, slides],
  );

  // Navigation functions
  const goToSlide = useCallback(
    newIndex => {
      if (isTransitioning || !slides?.length) {
        return;
      }

      // Note: stopAllVideos() is called AFTER the transition completes
      // in the video management effect in autoplayCarousel.js

      // Clear any existing transition timeout to prevent race conditions
      if (transitionTimeoutRef.current) {
        clearTimeout(transitionTimeoutRef.current);
        transitionTimeoutRef.current = null;
      }

      setIsTransitioning(true);

      // Calculate the actual slide index (0-based)
      const nextSlideIndex =
        ((newIndex % slides.length) + slides.length) % slides.length;

      // Calculate transform index (includes duplicates)
      // Priority: check simple +1/-1 steps first, then wrap cases
      // This prevents 2-slide carousels from incorrectly matching wrap conditions
      let nextTransformIndex;

      if (newIndex === activeSlideIndex + 1) {
        // Moving forward one step
        nextTransformIndex = currentTransformIndex + 1;
      } else if (newIndex === activeSlideIndex - 1) {
        // Moving backward one step
        nextTransformIndex = currentTransformIndex - 1;
      } else if (activeSlideIndex === slides.length - 1 && newIndex === 0) {
        // Wrapping from last to first (forward via autoplay/next at boundary)
        nextTransformIndex = currentTransformIndex + 1;
      } else if (activeSlideIndex === 0 && newIndex === slides.length - 1) {
        // Wrapping from first to last (backward via prev at boundary)
        nextTransformIndex = currentTransformIndex - 1;
      } else {
        // Direct navigation to specific slide (e.g., pagination click)
        nextTransformIndex = newIndex + 1; // +1 to account for duplicate at start
      }

      // Determine parallax transition direction based on transform movement
      let direction = null;
      if (nextTransformIndex > currentTransformIndex) {
        direction = 'next';
      } else if (nextTransformIndex < currentTransformIndex) {
        direction = 'prev';
      }
      setTransitionDirection(direction);

      setActiveSlideIndex(nextSlideIndex);
      setCurrentTransformIndex(nextTransformIndex);

      clearAutoplayTimers(timerRef, timeLeftRef);

      // Reset progress to 0 when changing slides
      if (progressCircle.current) {
        progressCircle.current.style.setProperty('--progress', 0);
      }

      // Store the timeout reference to allow cancellation
      // When reduced motion is preferred, transition is instant so use minimal delay
      const transitionDelay = prefersReducedMotion ? 0 : TRANSITION_DURATION;
      transitionTimeoutRef.current = setTimeout(() => {
        // Handle infinite scroll reset
        if (nextTransformIndex >= slides.length + 1) {
          // We've moved past the last duplicate, reset to first real slide
          setCurrentTransformIndex(1);
        } else if (nextTransformIndex <= 0) {
          // We've moved before the first duplicate, reset to last real slide
          setCurrentTransformIndex(slides.length);
        }

        setIsTransitioning(false);
        setTransitionDirection(null);
        updateFocusableElements(nextSlideIndex);
        transitionTimeoutRef.current = null;
      }, transitionDelay);
    },
    [
      isTransitioning,
      slides,
      activeSlideIndex,
      currentTransformIndex,
      prefersReducedMotion,
      setIsTransitioning,
      setTransitionDirection,
      setActiveSlideIndex,
      setCurrentTransformIndex,
      timerRef,
      timeLeftRef,
      progressCircle,
      updateFocusableElements,
      transitionTimeoutRef,
    ],
  );

  const goToNextSlide = useCallback(() => {
    goToSlide(activeSlideIndex + 1);
  }, [activeSlideIndex, goToSlide]);

  const goToPrevSlide = useCallback(() => {
    goToSlide(activeSlideIndex - 1);
  }, [activeSlideIndex, goToSlide]);

  // Autoplay logic
  const startAutoplay = useCallback(() => {
    if (!isAutoPlaying || isTransitioning) {
      return;
    }

    const currentSlide = slides[activeSlideIndex];
    const delay = getSlideDelay(currentSlide) || DEFAULT_IMAGE_SLIDE_DELAY;

    clearAutoplayTimers(timerRef, timeLeftRef);

    // Get current progress (either stored from pause or 0 for new slide)
    const currentProgress = progressCircle.current
      ? parseFloat(
          progressCircle.current.style.getPropertyValue('--progress') || '0',
        )
      : 0;

    // Calculate remaining time based on current progress
    const elapsed = currentProgress * delay;
    const remainingDelay = Math.max(0, delay - elapsed);

    // Timer for advancing slide
    timerRef.current = setTimeout(() => {
      if (isAutoPlaying) {
        goToNextSlide();
      }
    }, remainingDelay);

    // Use requestAnimationFrame for smoother progress updates
    const startTime = performance.now() - elapsed; // Adjust start time for current progress
    const updateProgress = () => {
      const elapsedTotal = performance.now() - startTime;
      const remaining = Math.max(0, delay - elapsedTotal);

      // Update progress circle
      const progress = 1 - remaining / delay;
      if (progressCircle.current) {
        progressCircle.current.style.setProperty('--progress', progress);
      }

      if (remaining > 0) {
        timeLeftRef.current = requestAnimationFrame(updateProgress);
      }
    };

    timeLeftRef.current = requestAnimationFrame(updateProgress);
  }, [
    isAutoPlaying,
    isTransitioning,
    activeSlideIndex,
    slides,
    getSlideDelay,
    goToNextSlide,
    timerRef,
    timeLeftRef,
    progressCircle,
  ]);

  const stopAutoplay = useCallback(() => {
    clearAutoplayTimers(timerRef, timeLeftRef);
    // Note: Don't reset progress - it's preserved for resume
    // Progress resets to 0 only when slide changes in goToSlide
  }, [timerRef, timeLeftRef]);

  return {
    getActiveSlideVideoPlayer,
    goToNextSlide,
    goToPrevSlide,
    goToSlide,
    // Slide helpers
    isActiveSlideVideo,
    pauseVideo,
    restartVideo,
    resumeVideo,
    // Autoplay functions
    startAutoplay,
    stopAllVideos,
    stopAutoplay,
    stopInactiveVideos,
    // Helper functions
    updateFocusableElements,
  };
};
